import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { BaseThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import axios from 'axios';
import SnackbarUtils from './SnackbarUtils';

type NestJsError = { message: string | string[] };
type RejectWithValue<RV> = ReturnType<BaseThunkAPI<unknown, unknown, Dispatch<AnyAction>, RV>['rejectWithValue']>;

const DEFAULT_FALLBACK = 'An unexpected error occurred!';
export const CANCEL_MESSAGE = 'Canceled';

const isNestJsError = (data: unknown): data is NestJsError => {
  if (typeof data !== 'object' || data === null || !('message' in data)) return false;
  const msg = (data as { message: unknown }).message;
  return typeof msg === 'string' || (Array.isArray(msg) && msg.every(m => typeof m === 'string'));
};

type ParseApiErrorOptions = {
  error?: unknown;
  fallback?: string;
};

/**
 * Runtime- and type-safe parsing of NestJS API errors returned from Axios requests.
 *
 * @example
 * const message = parseApiError({ error, fallback: 'Custom fallback message' });
 */
export const parseApiError = ({ error, fallback = DEFAULT_FALLBACK }: ParseApiErrorOptions = {}): string => {
  if (axios.isCancel(error)) return CANCEL_MESSAGE;

  if (!axios.isAxiosError(error)) return fallback;

  const data = error.response?.data;
  if (!isNestJsError(data)) return fallback;

  const message = data.message;
  if (Array.isArray(message)) return message.join(', ');
  return message;
};

type RejectWithApiErrorOptions<S, E, RV> = {
  thunkAPI: BaseThunkAPI<S, E, Dispatch<AnyAction>, RV>;
} & ParseApiErrorOptions;

/**
 * Helper function to reject a thunk with a parsed API error message.
 *
 * @example
 * return rejectWithApiError({ thunkAPI, error, fallback: 'Custom fallback message' });
 */
export const rejectWithApiError = <S, E, RV>({
  thunkAPI,
  ...options
}: RejectWithApiErrorOptions<S, E, RV>): RejectWithValue<RV> => {
  const message = parseApiError(options);
  if (message === CANCEL_MESSAGE) thunkAPI.abort(CANCEL_MESSAGE);
  return thunkAPI.rejectWithValue(message as unknown as RV);
};

/**
 * Helper function to show a snackbar with a parsed API error message.
 *
 * @example
 * showApiErrorSnackbar({ error, fallback: 'Custom fallback message' });
 */
export const showApiErrorSnackbar = (options: ParseApiErrorOptions = {}): void => {
  const message = parseApiError(options);
  setTimeout(() => {
    SnackbarUtils.error(message);
  });
};
